import { useEffect, useState } from 'react';
import type { BudgetFeatureView, BudgetView as Budget } from '@alfred/shared';
import { api, describeError } from './api.js';

type OnLimit = BudgetFeatureView['onLimit'];

/** What happens when a feature's budget runs out. */
const ON_LIMIT: Record<OnLimit, { short: string; long: string }> = {
  pause: { short: 'pauses', long: 'Pause until the 1st' },
  ask: { short: 'asks you', long: 'Ask me to raise it' },
  warn: { short: 'keeps going', long: 'Keep going, just warn me' },
};

const usd = (n: number) => `$${n.toFixed(n > 0 && n < 0.1 ? 3 : n % 1 === 0 ? 0 : 2)}`;

/**
 * Anthony's spending plan: a monthly budget per feature (what it buys, what's been spent, where
 * it's heading) and fixed costs, adding up to the most Alfred can cost this month. Change numbers
 * here, or tell Alfred in plain words ("1 edited video a day") and he proposes the budgets.
 */
export function BudgetView() {
  const [data, setData] = useState<Budget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .budget()
      .then(setData)
      .catch((err: unknown) => setError(describeError(err)));
  }, []);

  const save = async (fn: () => Promise<Budget | null>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await fn();
      if (next) setData(next);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="view">{error ? <p className="small">{error}</p> : null}</div>;
  const { totals } = data;
  return (
    <div className="view">
      <div className="tiles">
        <div className="tile">
          <span className="tile-label">Spent in {data.month.split(' ')[0]}</span>
          <span className="tile-value">{usd(totals.spentUsd + totals.fixedUsd)}</span>
          <span className="tile-sub">
            {usd(totals.spentUsd)} usage + {usd(totals.fixedUsd)} fixed
          </span>
        </div>
        <div className="tile">
          <span className="tile-label">Heading for</span>
          <span className="tile-value">{usd(totals.projectedUsd)}</span>
          <span className="tile-sub">
            at this pace (day {data.dayOfMonth} of {data.daysInMonth})
          </span>
        </div>
        <div className="tile">
          <span className="tile-label">Most it can cost</span>
          <span className="tile-value">{usd(totals.maxUsd)}</span>
          <span className="tile-sub">all budgets + fixed costs</span>
        </div>
      </div>

      <p className="muted small budget-hint">
        Set a monthly budget per feature. When one runs out, that feature pauses until the 1st and
        everything else keeps working. You can also tell Alfred what you want, like “1 edited video
        a day”, and he’ll work out the budgets for you to approve. Daily safety limit on all usage:{' '}
        {usd(data.dailyCapUsd)}.
      </p>
      {error && <p className="small">{error}</p>}

      <section className="panel">
        <h2 className="panel-title">Features</h2>
        <ul className="budget-list">
          {data.features.map((f) => (
            <FeatureRow
              key={f.id}
              f={f}
              busy={busy}
              onSave={(monthlyUsd, basis, onLimit) =>
                void save(() => api.setBudget(f.id, monthlyUsd, basis, onLimit))
              }
            />
          ))}
        </ul>
      </section>

      <section className="panel">
        <h2 className="panel-title">Fixed costs</h2>
        <ul className="budget-list">
          {data.fixed.map((x) => (
            <FixedRow
              key={x.id}
              label={x.label}
              amount={x.monthlyUsd}
              busy={busy}
              onSave={(label, monthlyUsd) =>
                void save(() => api.setFixedCost({ id: x.id, label, monthlyUsd }))
              }
              onRemove={() => void save(() => api.removeFixedCost(x.id))}
            />
          ))}
          <FixedRow
            label=""
            amount={0}
            busy={busy}
            adding
            onSave={(label, monthlyUsd) => void save(() => api.setFixedCost({ label, monthlyUsd }))}
          />
        </ul>
      </section>
    </div>
  );
}

function FeatureRow({
  f,
  busy,
  onSave,
}: {
  f: BudgetFeatureView;
  busy: boolean;
  onSave: (monthlyUsd: number | null, basis: string | null, onLimit: OnLimit) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(f.budgetUsd === null ? '' : String(f.budgetUsd));
  const [basis, setBasis] = useState(f.basis ?? '');
  const [onLimit, setOnLimit] = useState<OnLimit>(f.onLimit);
  const pct = f.budgetUsd ? Math.min(100, (f.spentUsd / f.budgetUsd) * 100) : 0;
  const over = f.budgetUsd !== null && f.spentUsd >= f.budgetUsd && f.budgetUsd > 0;

  return (
    <li className="budget-row">
      <div className="budget-top">
        <div>
          <strong>{f.name}</strong>
          {f.planned && <span className="tag">not built yet</span>}
          <div className="muted small">{f.description}</div>
        </div>
        <div className="budget-amount">
          <span className={over ? 'bad' : ''}>{usd(f.spentUsd)}</span>
          <span className="muted"> / {f.budgetUsd === null ? 'no limit' : usd(f.budgetUsd)}</span>
        </div>
      </div>
      {f.budgetUsd !== null && f.budgetUsd > 0 && (
        <div className="bar" aria-hidden="true">
          <div className={`bar-fill ${over ? 'bar-over' : ''}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="muted small">
        {f.basis && <>For: {f.basis} · </>}
        {f.buys && <>{f.buys} · </>}
        {f.spentUsd > 0 && <>heading for {usd(f.projectedUsd)} · </>}
        {f.runsOutOn && (
          <span className={f.runsOutOn === 'used up' ? 'bad' : 'warn'}>
            {f.runsOutOn === 'used up' ? 'used up' : `runs out ~${f.runsOutOn}`} ·{' '}
          </span>
        )}
        {ON_LIMIT[f.onLimit].short} at 100% ·{' '}
        {f.units
          .map((u) => `${u.name} ${usd(u.usd)}${u.source === 'estimate' ? ' (est.)' : ''}`)
          .join(', ')}
      </div>
      {editing ? (
        <div className="budget-edit">
          <label className="small">
            $
            <input
              className="money-input"
              inputMode="decimal"
              value={amount}
              placeholder="no limit"
              aria-label={`${f.name} monthly budget`}
              onChange={(e) => setAmount(e.target.value)}
            />
            /month
          </label>
          <label className="small">
            When it runs out:{' '}
            <select
              className="money-input limit-select"
              value={onLimit}
              aria-label={`What happens when the ${f.name} budget runs out`}
              onChange={(e) => setOnLimit(e.target.value as OnLimit)}
            >
              {(Object.keys(ON_LIMIT) as OnLimit[]).map((k) => (
                <option key={k} value={k}>
                  {ON_LIMIT[k].long}
                </option>
              ))}
            </select>
          </label>
          <input
            className="reject-reason"
            value={basis}
            maxLength={200}
            placeholder="What it’s for (optional), e.g. 1 video a day"
            aria-label={`${f.name} budget reason`}
            onChange={(e) => setBasis(e.target.value)}
          />
          <div className="row-gap">
            <button
              className="btn btn-small btn-primary"
              disabled={busy || (amount.trim() !== '' && !(Number(amount) >= 0))}
              onClick={() => {
                onSave(amount.trim() === '' ? null : Number(amount), basis.trim() || null, onLimit);
                setEditing(false);
              }}
            >
              Save
            </button>
            <button className="btn btn-small" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="link-btn small" onClick={() => setEditing(true)}>
          Change budget
        </button>
      )}
    </li>
  );
}

function FixedRow({
  label,
  amount,
  busy,
  adding,
  onSave,
  onRemove,
}: {
  label: string;
  amount: number;
  busy: boolean;
  adding?: boolean;
  onSave: (label: string, monthlyUsd: number) => void;
  onRemove?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(label);
  const [value, setValue] = useState(adding ? '' : String(amount));

  if (!editing) {
    return adding ? (
      <li>
        <button className="link-btn small" onClick={() => setEditing(true)}>
          + Add a fixed cost
        </button>
      </li>
    ) : (
      <li className="budget-top">
        <span>{label}</span>
        <span>
          {usd(amount)}/month{' '}
          <button className="link-btn small" onClick={() => setEditing(true)}>
            Edit
          </button>
        </span>
      </li>
    );
  }
  return (
    <li className="budget-edit">
      <input
        className="reject-reason"
        value={name}
        placeholder="e.g. Apify subscription"
        aria-label="Fixed cost name"
        onChange={(e) => setName(e.target.value)}
      />
      <label className="small">
        $
        <input
          className="money-input"
          inputMode="decimal"
          value={value}
          aria-label="Monthly amount"
          onChange={(e) => setValue(e.target.value)}
        />
        /month
      </label>
      <div className="row-gap">
        <button
          className="btn btn-small btn-primary"
          disabled={busy || !name.trim() || !(Number(value) >= 0) || value.trim() === ''}
          onClick={() => {
            onSave(name.trim(), Number(value));
            setEditing(false);
            if (adding) {
              setName('');
              setValue('');
            }
          }}
        >
          Save
        </button>
        {onRemove && (
          <button className="btn btn-small btn-danger-outline" disabled={busy} onClick={onRemove}>
            Remove
          </button>
        )}
        <button className="btn btn-small" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </li>
  );
}
